import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  useGetConfigDetailsQuery,
  useUpdateProjectMutation,
} from "@/features/bughurd/api";
import type {
  IntegrationState,
  JiraConfigItem,
  AzureBoardsConfigItem,
} from "../types";

const defaultFieldConfig = {
  epic: null,
  issueType: "TASK",
  isAutomaticCreateJira: false,
  isCreateComment: false,
  isSyncComment: false,
  syncFromJira: false,
  completedStatus: null,
  completedColumn: null,
};

const defaultAzureFieldConfig = {
  isAutomaticCreateAzure: false,
  isCreateComment: false,
  isSyncComment: false,
  syncFromAzure: false,
};

const initialState: IntegrationState = {
  jiraConfig: [],
  azureBoardsConfig: [],
  communicationChannel: "NONE",
  communicationChannelData: null,
};

export function useIntegrationState(
  projectDetails: any,
  platform: string,
  open: boolean,
) {
  const { id } = useParams();
  const { t } = useTranslation(["bugherd"]);
  const [state, setState] = useState<IntegrationState>(initialState);
  const [isJiraConfigured, setIsJiraConfigured] = useState(false);
  const [isAzureConfigured, setIsAzureConfigured] = useState(false);

  const { data: configDataList, isLoading: isLoadingConfig } =
    useGetConfigDetailsQuery({});
  const configData = configDataList?.data?.decryptedData;

  const [updateProject, { isLoading: isSaving }] = useUpdateProjectMutation();

  // Initialize state from projectDetails
  useEffect(() => {
    if (!projectDetails) return;

    let jiraConfig: JiraConfigItem[] = [];
    let azureBoardsConfig: AzureBoardsConfigItem[] = [];

    // Handle new integrationConfig format
    if (
      projectDetails?.integrationConfig &&
      projectDetails?.integrationType
    ) {
      const config = projectDetails.integrationConfig;

      if (projectDetails.integrationType === "JIRA") {
        jiraConfig = [
          {
            key: config.projectKey,
            projectId: config.projectId,
            projectData: config.projectData || {},
            defaultFields: config.defaultFields || {},
            fieldConfig: config.fieldConfig || {},
            syncFromJira: config.syncFrom || false,
            syncToJira: config.syncTo || false,
            syncCommentFromJira: config.syncCommentFrom || false,
            syncCommentToJira: config.syncCommentTo || false,
          },
        ];
      } else if (projectDetails.integrationType === "AZURE") {
        azureBoardsConfig = [
          {
            projectId: config.projectId,
            projectKey: config.projectKey || "",
            projectData: config.projectData || {},
            defaultFields: config.defaultFields || {},
            fieldConfig: config.fieldConfig || {},
            organization: config.organization || "",
            projectName: config.projectName || "",
            syncFromAzure: config.syncFrom || false,
            syncToAzure: config.syncTo || false,
            syncCommentFromAzure: config.syncCommentFrom || false,
            syncCommentToAzure: config.syncCommentTo || false,
          },
        ];
      }
    } else {
      // Fallback to legacy format
      jiraConfig = projectDetails?.jiraConfig || [];
      azureBoardsConfig = projectDetails?.azureBoardsConfig || [];
    }

    const communicationChannel =
      projectDetails?.communicationChannel || "NONE";

    let communicationChannelData = null;
    if (
      communicationChannel !== "NONE" &&
      projectDetails?.communicationChannelData
    ) {
      const webhookUrl = projectDetails.communicationChannelData.webhookUrl;
      communicationChannelData = {
        webhookUrl: Array.isArray(webhookUrl) ? webhookUrl[0] : webhookUrl,
      };
    }

    setState({
      jiraConfig,
      azureBoardsConfig,
      communicationChannel,
      communicationChannelData,
    });
  }, [projectDetails, platform]);

  // Detect configured integrations from company config
  useEffect(() => {
    if (Array.isArray(configData) && configData?.length > 0) {
      setIsJiraConfigured(!!configData.find((c) => c.type === "JIRA"));
      setIsAzureConfigured(!!configData.find((c) => c.type === "AZURE"));
    } else {
      setIsJiraConfigured(false);
      setIsAzureConfigured(false);
    }
  }, [configData, open]);

  // --- JIRA handlers ---

  const handleProjectChange = (_: any, newValue: any) => {
    setState((prev) => {
      if (!newValue) {
        return { ...prev, jiraConfig: [] };
      }

      const prevProject = prev.jiraConfig.find((p) => p.key === newValue.key);

      const updatedJiraConfig: JiraConfigItem[] = [
        {
          key: newValue.key,
          projectId: newValue.id,
          projectData: newValue,
          defaultFields: prevProject ? prevProject.defaultFields : {},
          fieldConfig: prevProject
            ? prevProject.fieldConfig
            : { ...defaultFieldConfig },
          syncToJira: prevProject ? prevProject.syncToJira : false,
          syncCommentToJira: prevProject
            ? prevProject.syncCommentToJira
            : false,
          syncCommentFromJira: prevProject
            ? prevProject.syncCommentFromJira
            : false,
          syncFromJira: prevProject ? prevProject.syncFromJira : false,
        },
      ];

      return { ...prev, jiraConfig: updatedJiraConfig };
    });
  };

  const updateFieldConfig = (
    key: string,
    fieldKey: string,
    value?: string | boolean,
    dataType?: string,
  ) => {
    const processedValue = dataType === "boolean" ? Boolean(value) : value;

    setState((prev) => {
      const updatedJiraConfig = prev.jiraConfig.map((config) => {
        if (config.key === key) {
          const updatedConfig = {
            ...config,
            fieldConfig: {
              ...config.fieldConfig,
              [fieldKey]: processedValue,
            },
          };
          if (fieldKey === "isAutomaticCreateJira") {
            updatedConfig.syncToJira = processedValue as boolean;
          } else if (fieldKey === "isCreateComment") {
            updatedConfig.syncCommentToJira = processedValue as boolean;
          } else if (fieldKey === "isSyncComment") {
            updatedConfig.syncCommentFromJira = processedValue as boolean;
          } else if (fieldKey === "syncFromJira") {
            updatedConfig.syncFromJira = processedValue as boolean;
          }
          return updatedConfig;
        }
        return config;
      });

      return { ...prev, jiraConfig: updatedJiraConfig };
    });
  };

  const handleDefaultFieldChange = (
    key: string,
    fieldName?: string,
    fieldKey?: string,
    value?: string | boolean,
    dataType?: string,
    allowedValues?: any,
  ) => {
    const processedValue = dataType === "number" ? Number(value) : value;

    setState((prev) => {
      const updatedJiraConfig = prev.jiraConfig.map((config) => {
        if (config.key === key) {
          return {
            ...config,
            defaultFields: {
              ...config?.defaultFields,
              [fieldName!]: {
                ...config?.defaultFields?.[fieldName!],
                [fieldKey!]: processedValue,
                method: Boolean(value),
                type: dataType,
                ...(allowedValues && { allowedValues }),
              },
            },
          };
        }
        return config;
      });

      return { ...prev, jiraConfig: updatedJiraConfig };
    });
  };

  // --- AZURE handlers ---

  const handleAzureProjectChange = (_: any, newValue: any) => {
    setState((prev) => {
      if (!newValue) {
        return { ...prev, azureBoardsConfig: [] };
      }

      const prevConfig = prev.azureBoardsConfig.find(
        (p) => p.projectId === newValue.id,
      );
      const existingOrg = prev.azureBoardsConfig?.[0]?.organization || "";

      const updatedAzureConfig: AzureBoardsConfigItem[] = [
        {
          organization: prevConfig ? prevConfig.organization : existingOrg,
          projectName: newValue.name,
          projectId: newValue.id,
          projectData: newValue,
          fieldConfig: prevConfig
            ? prevConfig.fieldConfig
            : { ...defaultAzureFieldConfig },
          syncToAzure: prevConfig ? prevConfig.syncToAzure : false,
          syncCommentToAzure: prevConfig
            ? prevConfig.syncCommentToAzure
            : false,
          syncCommentFromAzure: prevConfig
            ? prevConfig.syncCommentFromAzure
            : false,
          syncFromAzure: prevConfig ? prevConfig.syncFromAzure : false,
          defaultFields: prevConfig ? prevConfig.defaultFields : {},
        },
      ];

      return { ...prev, azureBoardsConfig: updatedAzureConfig };
    });
  };

  const updateAzureFieldConfig = (
    projectId: string,
    fieldKey: string,
    value?: string | boolean,
    dataType?: string,
  ) => {
    const processedValue = dataType === "boolean" ? Boolean(value) : value;

    setState((prev) => {
      const updatedAzureConfig = prev.azureBoardsConfig.map((config) => {
        if (config.projectId === projectId) {
          const updatedConfig = {
            ...config,
            fieldConfig: {
              ...config.fieldConfig,
              [fieldKey]: processedValue,
            },
          };
          if (fieldKey === "isAutomaticCreateAzure") {
            updatedConfig.syncToAzure = processedValue as boolean;
          } else if (fieldKey === "isCreateComment") {
            updatedConfig.syncCommentToAzure = processedValue as boolean;
          } else if (fieldKey === "isSyncComment") {
            updatedConfig.syncCommentFromAzure = processedValue as boolean;
          } else if (fieldKey === "syncFromAzure") {
            updatedConfig.syncFromAzure = processedValue as boolean;
          }
          return updatedConfig;
        }
        return config;
      });

      return { ...prev, azureBoardsConfig: updatedAzureConfig };
    });
  };

  const handleAzureOrgChange = (value: string) => {
    setState((prev) => {
      const updated = [...(prev.azureBoardsConfig || [])];
      if (updated.length === 0) {
        updated.push({
          organization: "",
          projectName: "",
          projectId: "",
          projectData: {},
          fieldConfig: { ...defaultAzureFieldConfig },
          syncFromAzure: false,
          syncToAzure: false,
          syncCommentFromAzure: false,
          syncCommentToAzure: false,
          defaultFields: {},
        });
      }
      updated[0] = { ...updated[0], organization: value };
      return { ...prev, azureBoardsConfig: updated };
    });
  };

  // --- Communication channel handler ---

  const handleWebhookChange = (newValue: any) => {
    if (!newValue) {
      setState((prev) => ({
        ...prev,
        communicationChannel: "NONE",
        communicationChannelData: null,
      }));
    } else {
      const webhookUrl = Array.isArray(newValue.webhookUrl)
        ? newValue.webhookUrl[0]
        : newValue.webhookUrl;

      setState((prev) => ({
        ...prev,
        communicationChannel: newValue.type,
        communicationChannelData: { webhookUrl },
      }));
    }
  };

  // --- Save ---

  const handleSave = async () => {
    if (!id) return;

    try {
      const { jiraConfig, azureBoardsConfig } = state;

      // Determine integration type and config based on platform
      let integrationType = projectDetails?.integrationType || "NONE";
      let integrationConfig = projectDetails?.integrationConfig || null;

      if (platform === "JIRA" && jiraConfig?.length > 0) {
        integrationType = "JIRA";
        const config = jiraConfig[0];
        integrationConfig = {
          projectId: config.projectId,
          projectKey: config.key,
          projectData: config.projectData || {},
          fieldConfig: config.fieldConfig || {},
          defaultFields: config.defaultFields || {},
          projectName: config.projectData?.name || "",
          syncFrom: config.syncFromJira || false,
          syncTo: config.syncToJira || false,
          syncCommentFrom: config.syncCommentFromJira || false,
          syncCommentTo: config.syncCommentToJira || false,
        };
      } else if (platform === "AZURE" && azureBoardsConfig?.length > 0) {
        integrationType = "AZURE";
        const config = azureBoardsConfig[0];
        integrationConfig = {
          projectId: config.projectId,
          projectKey: config.projectKey || "",
          projectData: config.projectData || {},
          fieldConfig: config.fieldConfig || {},
          defaultFields: config.defaultFields || {},
          organization: config.organization || "",
          projectName: config.projectName || config.projectData?.name || "",
          syncFrom: config.syncFromAzure || false,
          syncTo: config.syncToAzure || false,
          syncCommentFrom: config.syncCommentFromAzure || false,
          syncCommentTo: config.syncCommentToAzure || false,
        };
      } else if (platform === "ASANA") {
        integrationType = "ASANA";
      }

      // For communication channels, preserve existing project management integration
      const communicationChannel =
        platform === "SLACK" || platform === "TEAMS"
          ? state.communicationChannel
          : projectDetails?.communicationChannel || "NONE";

      const communicationChannelData =
        platform === "SLACK" || platform === "TEAMS"
          ? communicationChannel === "NONE"
            ? null
            : state.communicationChannelData || { webhookUrl: "" }
          : projectDetails?.communicationChannelData || null;

      // For PM integrations, preserve existing communication channel
      if (["JIRA", "AZURE", "ASANA"].includes(platform)) {
        // integrationType/integrationConfig already set above
      }

      const payload = {
        projectId: id,
        integrationType,
        integrationConfig,
        communicationChannel,
        communicationChannelData,
        // Keep legacy fields for backward compatibility
        jiraConfig: platform === "JIRA" ? jiraConfig : projectDetails?.jiraConfig || [],
        azureBoardsConfig: platform === "AZURE" ? azureBoardsConfig : projectDetails?.azureBoardsConfig || [],
      };

      const res = await updateProject(payload);
      if ((res as any)?.data) {
        toast.success(t("Integration updated successfully"));
        return true;
      } else {
        toast.error(t("Failed to update integration"));
        return false;
      }
    } catch (error) {
      toast.error(t("Operation failed"));
      return false;
    }
  };

  return {
    state,
    setState,
    isJiraConfigured,
    isAzureConfigured,
    configData,
    isLoadingConfig,
    handleSave,
    isSaving,
    // Jira
    handleProjectChange,
    updateFieldConfig,
    handleDefaultFieldChange,
    // Azure
    handleAzureProjectChange,
    updateAzureFieldConfig,
    handleAzureOrgChange,
    // Communication
    handleWebhookChange,
  };
}
